import {
  ComponentVariantsToSave,
  MappedNode,
  NodeMap,
  // VariableConsumptionMap,
} from '@app-velocity/common/types';

import { createCoreMethod } from '../../utils/createCoreMethod';
import { shallowFindInContainer } from '../../utils/shallowFind';
import { getNodesMap } from './getNodesMap';
import { SimpleTextNode } from '../../types';
import { safeAppendNode } from '../general/safeAppendNode';
import { isNodeVisible } from '../../utils/isNodeVisible';

const generatePointsFromNodes = (
  nodes: MappedNode[],
  nodeId: string,
  flowPageId: string,
  nodeType: 'page' | 'partial' | 'dynamic' | 'variant',
  clonedIdsMap: { [clonedId: string]: string },
  originalIdsMap: { [clonedId: string]: string }
) => {
  const generatedPoints: { type: 'page'; point: string; pageId: string }[] = [];
  const generatedPartialPoints: {
    type: 'partial';
    point: string;
    pageId: string;
  }[] = [];
  const variants: ComponentVariantsToSave[] = [];
  const generatedDynamicNodePoints: {
    type: 'dynamic';
    point: string;
    pageId: string;
  }[] = [];
  const generatedVariantPoints: {
    type: 'variant';
    point: string;
    pageId: string;
  }[] = [];

  for (const node of nodes) {
    if (node.clonedIdsMap) {
      for (const key in node.clonedIdsMap) {
        clonedIdsMap[key] = node.clonedIdsMap[key];
      }
    }

    if (node.originalIdsMap) {
      for (const key in node.originalIdsMap) {
        originalIdsMap[key] = node.originalIdsMap[key];
      }
    }

    if (node.variants && node.variants.length > 0) {
      const partialId = ['dynamic', 'partial', 'variant'].includes(nodeType)
        ? nodeId
        : undefined;

      variants.push(
        ...node.variants.map((variant) => ({
          ...variant,
          partialId,
          x: node.x,
          y: node.y,
        }))
      );
    }

    if (node.generatedPoints) {
      for (const generatedPoint of node.generatedPoints) {
        generatedPoints.push({
          type: 'page',
          point: generatedPoint,
          pageId: flowPageId,
        });
      }
    }

    if (node.generatedPartialPoints) {
      for (const generatedPoint of node.generatedPartialPoints) {
        generatedPartialPoints.push({
          type: 'partial',
          point: generatedPoint,
          pageId: flowPageId,
        });
      }
    }

    if (node.generatedVariantPoints) {
      for (const generatedPoint of node.generatedVariantPoints) {
        generatedVariantPoints.push({
          type: 'variant',
          point: generatedPoint,
          pageId: flowPageId,
        });
      }
    }

    // node id needs to be checked in order not to create loop with dynamic nodes
    if (node.dynamic && node.id !== nodeId) {
      generatedDynamicNodePoints.push({
        type: 'dynamic',
        point: node.id,
        pageId: flowPageId,
      });
    }

    if (node.actions) {
      for (const action of node.actions!) {
        if (!action.to) continue;

        if (['overlay', 'swap-overlay'].includes(action.action)) {
          generatedPartialPoints.push({
            type: 'partial',
            point: action.to,
            pageId: flowPageId,
          });
        }

        if (action.action === 'navigate') {
          generatedPoints.push({
            type: 'page',
            point: action.to,
            pageId: flowPageId,
          });
        }
      }
    }
  }

  return {
    generatedPoints,
    generatedPartialPoints,
    generatedDynamicNodePoints,
    generatedVariantPoints,
    variants,
  };
};

const mapOriginalIdToCloned = (
  originalId: string,
  originalIdsMap: { [originalId: string]: string }
) => {
  return originalIdsMap[originalId];
};

export const getNextPoints = createCoreMethod(
  'getNextPoints',
  async (
    startingPoint: string,
    parentNodeId: string,
    dontSkipVariants = false
  ) => {
    // await figma.loadAllPagesAsync();
    const tempLayer = figma.createFrame();
    tempLayer.name = 'velocity-temp-data';
    tempLayer.resize(0, 0);
    tempLayer.x = -10000;
    tempLayer.y = -10000;
    tempLayer.backgrounds = [];

    // mapping between cloned id and original id
    const clonedIdsMap: { [clonedId: string]: string } = {};
    const originalIdsMap: { [originalId: string]: string } = {};

    const handledVariantIds: string[] = [];

    // console.log('START TO GETTING NODES');
    const allFigmaNodes = figma.currentPage.findAll((n) => isNodeVisible(n));
    const byId = new Map<string, SceneNode>();

    for (const node of allFigmaNodes) {
      byId.set(node.id, node);
    }

    // console.log('STOP TO GETTING NODES', allFigmaNodes.length);
    byId.set('velocity-temp-data', tempLayer);

    const variantsMap = new Map<
      string,
      {
        destinationIds: Map<string, string[]>;
        componentsMap: Map<string, ComponentNode>;
      }
    >();

    const variables = await figma.variables.getLocalVariablesAsync();

    let globalNodeMap: { [parentId: string]: NodeMap } = {};
    let globalTextNodes: SimpleTextNode[] = [];
    let globalWarnings: { nodeId: string; nodeName: string }[] = [];

    let parent: BaseNode | null = byId.get(parentNodeId) || null;

    if (!parent) {
      parent = figma.currentPage;
    }

    if (!parent)
      return {
        generatedPoints: [],
        generatedPartialPoints: [],
        generatedDynamicNodePoints: [],
        generatedVariantPoints: [],
        variants: [],
        globalNodeMap: {},
        globalTextNodes: [],
        originalIdsMap: {},
        clonedIdsMap: {},
      };

    let node: BaseNode | null = shallowFindInContainer(startingPoint, parent);

    if (!node) {
      node = byId.get(startingPoint) || null;
    }

    if (!node || !('clone' in node))
      return {
        generatedPoints: [],
        generatedPartialPoints: [],
        generatedDynamicNodePoints: [],
        generatedVariantPoints: [],
        variants: [],
        globalNodeMap: {},
        globalTextNodes: [],
        clonedIdsMap: {},
        originalIdsMap: {},
      };

    const clonedPage = node.clone();
    clonedPage.name = node.name;

    await safeAppendNode(clonedPage as FrameNode, tempLayer);
    byId.set(clonedPage.id, clonedPage as FrameNode);

    clonedIdsMap[clonedPage.id] = node.id;
    originalIdsMap[node.id] = clonedPage.id;

    // console.log('STARTING GETTING PAGE NODE MAP')
    const nodesMapForFirstPage = await getNodesMap(
      clonedPage,
      'page',
      clonedPage.id,
      handledVariantIds,
      byId,
      variantsMap,
      variables,
      dontSkipVariants
    );
    // console.log('STOPPED GETTING PAGE NODE MAP', nodesMapForFirstPage);

    // if(Object.keys(nodesMapForFirstPage)) throw new Error('lmao')

    globalNodeMap = {
      ...globalNodeMap,
      [clonedPage.id.replace(/[:;]/g, '-')]: nodesMapForFirstPage.nodeMap,
    };

    globalTextNodes = [
      ...globalTextNodes,
      ...nodesMapForFirstPage.mappedTextNodes,
    ];

    const {
      generatedPoints: nextPoints,
      generatedPartialPoints: nextPartialPoints,
      generatedDynamicNodePoints: nextDynamicNodePoints,
      generatedVariantPoints: nextGeneratedVariantPoints,
      variants: nextVariantPoints,
    } = generatePointsFromNodes(
      Object.values(nodesMapForFirstPage.nodeMap),
      clonedPage.id,
      clonedPage.id,
      'page',
      clonedIdsMap,
      originalIdsMap
    );
    // console.log('GOT STARTING POINT');
    const componentVariantsIDs = nextVariantPoints
      .map((variantsSet) => variantsSet.variants)
      .flat()
      .map((variant) => variant.id);
    handledVariantIds.push(...componentVariantsIDs);

    const generatedPoints: { pointId: string; pageId: string }[] = [];
    const generatedPartialPoints: { pointId: string; pageId: string }[] = [];
    const generatedVariantPoints: { pointId: string; pageId: string }[] = [];
    const variants: ComponentVariantsToSave[] = [];
    const generatedDynamicNodePoints: { pointId: string; pageId: string }[] =
      [];

    variants.push(...nextVariantPoints);

    generatedPoints.push({ pointId: node.id, pageId: clonedPage.id });

    const allNextPagePoints = nextPoints.filter(
      (nextPoint) => !generatedPoints.find((p) => p.pointId === nextPoint.point)
    );

    const allNextPartialPoints = nextPartialPoints.filter(
      (nextPoint) =>
        !generatedPartialPoints.find((p) => p.pointId === nextPoint.point)
    );

    const allNextDynamicPartialPoints = nextDynamicNodePoints.filter(
      (nextPoint) =>
        !generatedPartialPoints.find((p) => p.pointId === nextPoint.point)
    );

    const allNextVariantPoints = nextGeneratedVariantPoints.filter(
      (nextPoint) =>
        !generatedVariantPoints.find((p) => p.pointId === nextPoint.point)
    );

    const allNextPoints: {
      type: 'dynamic' | 'partial' | 'page' | 'variant';
      point: string;
      pageId: string;
    }[] = [
      ...allNextPagePoints,
      ...allNextPartialPoints,
      ...allNextDynamicPartialPoints,
      ...allNextVariantPoints,
    ];

    while (allNextPoints.length > 0) {
      const nextPoint = allNextPoints.shift();

      if (!nextPoint) break;

      let nextPointNode = byId.get(nextPoint.point);

      if (!nextPointNode) {
        nextPointNode = shallowFindInContainer(
          nextPoint.point,
          parent!
        ) as SceneNode;
      }

      if (!nextPointNode) {
        nextPointNode = figma.currentPage.findOne(
          (node) => node.id === nextPoint.point
        ) as SceneNode;
      }

      if (!nextPointNode) break;

      parent = nextPointNode.parent;

      // if (!('clone' in nextPointNode)) return;
      // Variant is already cloned
      const clonedNextNode =
        nextPoint.type === 'variant' ? nextPointNode : nextPointNode.clone();

      clonedNextNode.name = nextPointNode.name;

      await safeAppendNode(clonedNextNode as SceneNode, tempLayer);
      byId.set(clonedNextNode.id, clonedNextNode as SceneNode);

      // variants are setting this separately
      if (nextPoint.type !== 'variant') {
        clonedIdsMap[clonedNextNode.id] = nextPointNode.id;
        originalIdsMap[nextPointNode.id] = clonedNextNode.id;
      }

      const newNodesMap = await getNodesMap(
        clonedNextNode,
        nextPoint.type,
        nextPoint.type === 'page'
          ? originalIdsMap[nextPoint.point]
          : nextPoint.pageId,
        handledVariantIds,
        byId,
        variantsMap,
        variables,
        dontSkipVariants
      );

      const normalizedNextPointId = clonedNextNode.id.replace(/[:;]/g, '-');
      const normalizedNextPointOriginalId = nextPointNode.id.replace(
        /[:;]/g,
        '-'
      );

      globalNodeMap = {
        ...globalNodeMap,
        [normalizedNextPointId]: newNodesMap.nodeMap,
      };

      // patch to get correct position of dynamic nodes inside a page
      if (nextPoint.type === 'dynamic' && nextPoint.pageId) {
        const normalizedPageId = nextPoint.pageId.replace(/[:;]/g, '-');

        if (globalNodeMap[normalizedPageId][normalizedNextPointOriginalId]) {
          globalNodeMap[normalizedNextPointId][normalizedNextPointId] = {
            ...globalNodeMap[normalizedNextPointId][normalizedNextPointId],
            x: globalNodeMap[normalizedPageId][normalizedNextPointOriginalId].x,
            y: globalNodeMap[normalizedPageId][normalizedNextPointOriginalId].y,
          };
        }
      }

      globalWarnings = [...globalWarnings, ...(newNodesMap.warnings || [])];

      globalTextNodes = [...globalTextNodes, ...newNodesMap.mappedTextNodes];

      const {
        generatedPoints: newNextPoints,
        generatedPartialPoints: newNextPartialPoints,
        generatedDynamicNodePoints: newNextDynamicNodePoints,
        generatedVariantPoints: newNextGeneratedVariantPoints,
        variants: newNextVariantPoints,
      } = generatePointsFromNodes(
        Object.values(newNodesMap.nodeMap),
        clonedNextNode.id,
        ['dynamic', 'partial', 'variant'].includes(nextPoint.type)
          ? originalIdsMap[nextPoint.pageId]
          : originalIdsMap[nextPoint.point],
        nextPoint.type,
        clonedIdsMap,
        originalIdsMap
      );

      const componentVariantsIDs = newNextVariantPoints
        .map((variantsSet) => variantsSet.variants)
        .flat()
        .map((variant) => variant.id);
      handledVariantIds.push(...componentVariantsIDs);

      variants.push(...newNextVariantPoints);

      if (nextPoint.type === 'page') {
        generatedPoints.push({
          pointId: nextPoint.point,
          pageId: nextPoint.pageId,
        });
      } else if (nextPoint.type === 'partial') {
        generatedPartialPoints.push({
          pointId: nextPoint.point,
          pageId: nextPoint.pageId,
        });
      } else if (nextPoint.type === 'dynamic') {
        generatedDynamicNodePoints.push({
          pointId: nextPoint.point,
          pageId: nextPoint.pageId,
        });
      } else if (nextPoint.type === 'variant') {
        generatedVariantPoints.push({
          pointId: nextPoint.point,
          pageId: nextPoint.pageId,
        });
      }

      [
        ...newNextPoints,
        ...newNextPartialPoints,
        ...newNextDynamicNodePoints,
        ...newNextGeneratedVariantPoints,
      ].forEach((newNextPoint) => {
        if (
          !allNextPoints.find(
            (nextPoint) => nextPoint.point === newNextPoint.point
          ) &&
          ![
            ...generatedPoints,
            ...generatedPartialPoints,
            ...generatedDynamicNodePoints,
            ...generatedVariantPoints,
          ].find(
            (p) =>
              p.pointId === newNextPoint.point ||
              p.pointId === clonedIdsMap[newNextPoint.point]
          )
        ) {
          allNextPoints.push(newNextPoint);
        }
      });
    }

    const globalWarningsWithoutDuplicates = globalWarnings.filter(
      (warning, index) => {
        const firstIndex = globalWarnings.findIndex(
          (w) => w.nodeId === warning.nodeId
        );

        return firstIndex === index;
      }
    );

    for (const key in globalNodeMap) {
      const subMap = globalNodeMap[key];

      for (const subkey in subMap) {
        if (
          subMap[subkey] &&
          subMap[subkey].variants &&
          subMap[subkey].variants!.length > 0
        ) {
          const variants = subMap[subkey].variants;

          if (!variants) continue;

          for (const variantSet of variants) {
            if (variantSet.variants && variantSet.variants.length > 0) {
              for (const variant of variantSet.variants) {
                if (variant.actions.length === 0) continue;

                variant.actions = variant.actions.map((action) => {
                  if (action.action !== 'change' || !action.from) return action;

                  return {
                    ...action,
                    from: clonedIdsMap[action.from] || action.from,
                  };
                });
              }
            }
          }
        }
      }
    }

    return {
      generatedPoints: generatedPoints.map((p) =>
        mapOriginalIdToCloned(p.pointId, originalIdsMap)
      ),
      generatedPartialPoints: generatedPartialPoints.map((p) =>
        mapOriginalIdToCloned(p.pointId, originalIdsMap)
      ),
      generatedDynamicNodePoints: generatedDynamicNodePoints.map((p) =>
        mapOriginalIdToCloned(p.pointId, originalIdsMap)
      ),
      generatedVariantPoints: generatedVariantPoints.map((p) =>
        mapOriginalIdToCloned(p.pointId, originalIdsMap)
      ),
      variants,
      globalNodeMap,
      globalTextNodes,
      globalWarnings: globalWarningsWithoutDuplicates,
      clonedIdsMap,
      originalIdsMap,
    };
  }
);
